const EDITOR_ATTRIBUTE_PREFIXES = [
  'data-',
  'inkscape:',
  'sodipodi:',
  'sketch:',
  'serif:',
  'adobe:',
];

const EDITOR_ELEMENTS = new Set(['metadata', 'sodipodi:namedview']);

const ELEMENTS_WITH_CHILDREN = new Set([
  'svg',
  'g',
  'defs',
  'clipPath',
  'mask',
  'pattern',
  'symbol',
  'linearGradient',
  'radialGradient',
  'text',
  'tspan',
]);

const KNOWN_ELEMENTS = new Set([
  ...ELEMENTS_WITH_CHILDREN,
  'circle',
  'ellipse',
  'line',
  'path',
  'polygon',
  'polyline',
  'rect',
  'stop',
  'use',
  'title',
]);

const KNOWN_ATTRIBUTES = new Set([
  'aria-hidden',
  'aria-label',
  'clip-path',
  'clip-rule',
  'cx',
  'cy',
  'd',
  'dx',
  'dy',
  'fill',
  'fill-opacity',
  'fill-rule',
  'filter',
  'fx',
  'fy',
  'gradientTransform',
  'gradientUnits',
  'height',
  'href',
  'id',
  'mask',
  'offset',
  'opacity',
  'points',
  'preserveAspectRatio',
  'r',
  'role',
  'rx',
  'ry',
  'spreadMethod',
  'stop-color',
  'stop-opacity',
  'stroke',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'style',
  'transform',
  'viewBox',
  'width',
  'x',
  'x1',
  'x2',
  'xmlns',
  'y',
  'y1',
  'y2',
]);

const DEFAULT_ATTRIBUTES = new Map([
  ['fill', 'black'],
  ['fill-opacity', '1'],
  ['fill-rule', 'nonzero'],
  ['opacity', '1'],
  ['stroke', 'none'],
  ['stroke-dasharray', 'none'],
  ['stroke-dashoffset', '0'],
  ['stroke-linecap', 'butt'],
  ['stroke-linejoin', 'miter'],
  ['stroke-miterlimit', '4'],
  ['stroke-opacity', '1'],
  ['stroke-width', '1'],
  ['xmlns', 'http://www.w3.org/2000/svg'],
]);

const DEPRECATED_ATTRIBUTES = new Set([
  'baseProfile',
  'enable-background',
  'version',
  'xml:space',
  'xmlns:adobe',
  'xmlns:dc',
  'xmlns:inkscape',
  'xmlns:rdf',
  'xmlns:serif',
  'xmlns:sketch',
  'xmlns:sodipodi',
]);

function stripSvgProlog(content: string) {
  return content
    .replace(/^\uFEFF/, '')
    .replace(/<\?xml[\s\S]*?\?>/gi, '')
    .replace(/<!doctype[\s\S]*?>/gi, '')
    .trim();
}

function normalizeWhitespace(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

function isEditorAttribute(name: string) {
  return EDITOR_ATTRIBUTE_PREFIXES.some((prefix) => name.startsWith(prefix));
}

function isHiddenElement(element: Element) {
  const hidden = element.getAttribute('hidden');
  const display = element.getAttribute('display');
  const visibility = element.getAttribute('visibility');
  const opacity = element.getAttribute('opacity');
  const style = element.getAttribute('style')?.toLowerCase() ?? '';

  return (
    hidden !== null ||
    display === 'none' ||
    visibility === 'hidden' ||
    opacity === '0' ||
    /(?:^|;)\s*display\s*:\s*none\s*(?:;|$)/.test(style) ||
    /(?:^|;)\s*visibility\s*:\s*hidden\s*(?:;|$)/.test(style) ||
    /(?:^|;)\s*opacity\s*:\s*0\s*(?:;|$)/.test(style)
  );
}

function cleanAttributes(element: Element) {
  Array.from(element.attributes).forEach((attribute) => {
    const { name } = attribute;
    const value = normalizeWhitespace(attribute.value);

    if (name === 'xlink:href') {
      if (value && !element.hasAttribute('href')) {
        element.setAttribute('href', value);
      }
      element.removeAttribute(name);
      return;
    }

    if (
      !value ||
      isEditorAttribute(name) ||
      DEPRECATED_ATTRIBUTES.has(name) ||
      !KNOWN_ATTRIBUTES.has(name) ||
      DEFAULT_ATTRIBUTES.get(name) === value
    ) {
      element.removeAttribute(name);
      return;
    }

    if (element.tagName === 'svg' && (name === 'width' || name === 'height')) {
      element.removeAttribute(name);
      return;
    }

    if (value !== attribute.value) {
      element.setAttribute(name, value);
    }
  });
}

function cleanElement(element: Element) {
  const tagName = element.tagName;

  if (
    EDITOR_ELEMENTS.has(tagName) ||
    !KNOWN_ELEMENTS.has(tagName) ||
    isHiddenElement(element)
  ) {
    element.remove();
    return;
  }

  cleanAttributes(element);
}

function removeEmptyTextNodes(root: Element) {
  const walker = root.ownerDocument.createTreeWalker(root, 4);
  const emptyTextNodes: Text[] = [];

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (!node.textContent?.trim()) {
      emptyTextNodes.push(node);
    }
  }

  emptyTextNodes.forEach((node) => node.remove());
}

function collectReferences(root: Element) {
  const refs = new Set<string>();

  root.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach(({ value }) => {
      const normalizedValue = value.trim();
      const hashRef = normalizedValue.match(/^#(.+)$/);

      if (hashRef?.[1]) {
        refs.add(hashRef[1]);
      }

      for (const match of normalizedValue.matchAll(/url\(["']?#([^"')]+)["']?\)/g)) {
        refs.add(match[1]);
      }
    });
  });

  return refs;
}

function removeUnusedDefs(root: Element) {
  const refs = collectReferences(root);

  root.querySelectorAll('defs').forEach((defs) => {
    Array.from(defs.children).forEach((child) => {
      const id = child.getAttribute('id');
      if (id && !refs.has(id)) {
        child.remove();
      }
    });

    if (!defs.children.length && !defs.textContent?.trim()) {
      defs.remove();
    }
  });
}

function removeEmptyContainerElements(root: Element) {
  Array.from(root.querySelectorAll(Array.from(ELEMENTS_WITH_CHILDREN).join(',')))
    .reverse()
    .forEach((element) => {
      if (element.tagName !== 'svg' && !element.children.length && !element.textContent?.trim()) {
        element.remove();
      }
    });
}

function parseSvg(content: string) {
  if (typeof DOMParser === 'undefined') {
    return null;
  }

  const doc = new DOMParser().parseFromString(stripSvgProlog(content), 'image/svg+xml');
  const parserError = doc.querySelector('parsererror');

  if (parserError) {
    return null;
  }

  const svg = doc.documentElement;
  return svg?.tagName === 'svg' ? svg : null;
}

export function cleanSvg(content: string) {
  const svg = parseSvg(content);

  if (!svg) {
    return stripSvgProlog(content)
      .replace(/<(metadata|desc)\b[\s\S]*?<\/\1>/gi, '')
      .replace(/<svg\b([^>]*)>/gi, (tag) =>
        tag.replace(/\s(?:width|height)=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      )
      .trim();
  }

  svg.querySelectorAll('metadata,desc,sodipodi\\:namedview').forEach((element) => {
    element.remove();
  });

  removeEmptyTextNodes(svg);
  Array.from(svg.querySelectorAll('*')).forEach(cleanElement);
  cleanElement(svg);
  removeUnusedDefs(svg);
  removeEmptyContainerElements(svg);

  return new XMLSerializer().serializeToString(svg).trim();
}
